import {
  DEFAULT_FORM,
  DEFAULT_LAYOUT,
  DEFAULT_PALETTE,
  isKnownForm,
  isKnownLayout,
  isKnownPalette,
  paletteMode,
} from "../appearance/registry";
import type { AppearanceState } from "../appearance/types";
import { isKnownCurrency } from "../currencies/codes";
import { defaultSession } from "./defaults";
import {
  AMOUNT_PATTERN,
  MAX_QUOTES,
  SCHEMA_VERSION,
  STORAGE_KEY,
  WRITE_DEBOUNCE_MS,
  type Locale,
  type SessionState,
} from "./schema";

/**
 * localStorage throws, not just returns null: Safari private mode, disabled
 * storage, and full quota all raise. Every access goes through these two
 * helpers so a storage failure degrades to defaults instead of breaking the app.
 */
function readRaw(): string | null {
  try {
    return globalThis.localStorage?.getItem(STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
}

function writeRaw(value: string): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, value);
  } catch {
    // Persistence is a nice-to-have; losing it must never surface to the user.
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/* ------------------------------------------------------------------ *
 * Migration
 * ------------------------------------------------------------------ */

type Migration = (input: Record<string, unknown>) => Record<string, unknown> | null;

/**
 * Keyed by the version being migrated FROM. There is nothing to migrate yet at
 * v1; the machinery ships now so the first real bump is a one-line change
 * rather than a retrofit.
 */
const MIGRATIONS: Readonly<Record<number, Migration>> = {};

/**
 * Walks a stored payload up to the current version.
 * Returns null when there is no path — the caller then discards cleanly and
 * falls back to defaults, which is always better than rendering a broken state.
 */
function migrate(input: Record<string, unknown>): Record<string, unknown> | null {
  const storedVersion = typeof input["v"] === "number" ? input["v"] : 0;
  if (storedVersion === SCHEMA_VERSION) return input;
  // A payload from a NEWER build than this one. Do not guess at its shape.
  if (storedVersion > SCHEMA_VERSION) return null;

  let current = input;
  let version = storedVersion;
  while (version < SCHEMA_VERSION) {
    const step = MIGRATIONS[version];
    if (!step) return null;
    const next = step(current);
    if (!next) return null;
    current = next;
    version += 1;
  }
  return current;
}

/* ------------------------------------------------------------------ *
 * Per-field sanitising
 * ------------------------------------------------------------------ */

function sanitizeAppearance(value: unknown): AppearanceState {
  const source = isRecord(value) ? value : {};

  const palette =
    typeof source["palette"] === "string" && isKnownPalette(source["palette"])
      ? source["palette"]
      : DEFAULT_PALETTE;
  const form =
    typeof source["form"] === "string" && isKnownForm(source["form"])
      ? source["form"]
      : DEFAULT_FORM;
  const layout =
    typeof source["layout"] === "string" && isKnownLayout(source["layout"])
      ? source["layout"]
      : DEFAULT_LAYOUT;

  // Always re-derived from the registry, never trusted from storage: the stored
  // copy exists purely so the inline no-flash script can read it without
  // importing the registry, and a stale value there would flash the wrong
  // background colour.
  return { palette, form, layout, colorScheme: paletteMode(palette) };
}

function sanitizeQuotes(value: unknown, fallback: readonly string[]): readonly string[] {
  if (!Array.isArray(value)) return fallback;
  const seen = new Set<string>();
  const cleaned: string[] = [];
  for (const entry of value) {
    if (!isKnownCurrency(entry) || seen.has(entry)) continue;
    seen.add(entry);
    cleaned.push(entry);
    if (cleaned.length >= MAX_QUOTES) break;
  }
  return cleaned.length > 0 ? cleaned : fallback;
}

/**
 * Applies defaults field by field, NOT all-or-nothing. One bad value must cost
 * the user only that value — a corrupt currency code should not also reset the
 * amount they were typing or the appearance they chose.
 */
export function sanitizeSession(value: unknown, fallback?: SessionState): SessionState {
  const base = fallback ?? defaultSession();
  if (!isRecord(value)) return base;

  const migrated = migrate(value);
  if (!migrated) return base;

  const baseCode =
    isKnownCurrency(migrated["base"]) ? migrated["base"] : base.base;

  const rawAmount = migrated["amount"];
  const amount =
    typeof rawAmount === "string" && AMOUNT_PATTERN.test(rawAmount)
      ? rawAmount
      : base.amount;

  const locale: Locale =
    migrated["locale"] === "ar" || migrated["locale"] === "en"
      ? migrated["locale"]
      : base.locale;

  return {
    v: SCHEMA_VERSION,
    base: baseCode,
    amount,
    quotes: sanitizeQuotes(migrated["quotes"], base.quotes),
    locale,
    appearance: sanitizeAppearance(migrated["appearance"]),
  };
}

/* ------------------------------------------------------------------ *
 * Load / save
 * ------------------------------------------------------------------ */

/**
 * Synchronous by design. This is called at module init and its result is used
 * as React's INITIAL state — never rendered as defaults and corrected in a
 * useEffect, which is exactly the flash this architecture exists to prevent.
 */
export function loadSession(options?: {
  country?: string | undefined;
  language?: string | undefined;
}): SessionState {
  const fallback = defaultSession(options);
  const raw = readRaw();
  if (raw === null) return fallback;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return fallback;
  }
  return sanitizeSession(parsed, fallback);
}

let pending: SessionState | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;

/** Debounced write. Typing an amount must not hit storage on every keystroke. */
export function saveSession(state: SessionState): void {
  pending = state;
  if (timer !== null) clearTimeout(timer);
  timer = setTimeout(flushSession, WRITE_DEBOUNCE_MS);
}

/** Writes any pending state immediately. Safe to call when nothing is pending. */
export function flushSession(): void {
  if (timer !== null) {
    clearTimeout(timer);
    timer = null;
  }
  if (pending === null) return;
  const toWrite = pending;
  pending = null;
  writeRaw(JSON.stringify(toWrite));
}

/**
 * Mobile browsers kill backgrounded tabs without warning and often never fire
 * `beforeunload`, so a debounced write can simply be lost. `visibilitychange`
 * to hidden and `pagehide` are the two events that do reliably fire.
 */
export function installSessionFlushHandlers(): void {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushSession();
  });
  window.addEventListener("pagehide", flushSession);
}

/** Test seam: drops any debounced write without persisting it. */
export function resetPendingWrites(): void {
  if (timer !== null) clearTimeout(timer);
  timer = null;
  pending = null;
}
